/**
 * @imagine-readonly
 */

import { useMutation } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  useNavigate,
  useRouter,
  useSearch,
} from '@tanstack/react-router'
import { z } from 'zod'
import { AuthCard } from '@/components/auth/auth-card'
import { AuthForm } from '@/components/auth/auth-form'
import { AuthField } from '@/components/auth/auth-field'
import { signUpFn } from '@/server/functions/auth'
import { useServerFn } from '@tanstack/react-start'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

const searchSchema = z.object({
  redirect: z.string().optional(),
})

export const Route = createFileRoute('/_auth/sign-up')({
  component: SignUpPage,
  validateSearch: searchSchema,
})

const signUpSchema = z.object({
  pseudo: z.string().min(2, 'Pseudo is required').max(24, 'Pseudo is too long'),
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  emulatorUserId: z.string().max(64, 'User ID is too long').optional(),
})

function SignUpPage() {
  const search = useSearch({ from: Route.id })
  const navigate = useNavigate()
  const router = useRouter()
  const signUp = useServerFn(signUpFn)
  const form = useForm({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      pseudo: '',
      email: '',
      password: '',
      emulatorUserId: '',
    },
  })

  const signUpMutation = useMutation({
    mutationFn: async (data: z.infer<typeof signUpSchema>) => {
      await signUp({
        data: { ...data, redirect: search.redirect },
      })
    },
    onSuccess: async () => {
      // Invalidate router to refresh auth state
      await router.invalidate()
      // Navigate to the redirect destination if provided
      if (search.redirect) {
        await navigate({ to: search.redirect })
      }
    },

    onError: async (error: {
      status: number
      redirect: boolean
      message: string
    }) => {
      // Check if it's a redirect error (TanStack Start throws redirects as errors)
      if (
        error?.status === 302 ||
        error?.redirect ||
        error?.message?.includes('redirect')
      ) {
        // Invalidate router to refresh auth state
        await router.invalidate()
        // Navigate to the redirect destination if provided
        if (search.redirect) {
          await navigate({ to: search.redirect })
        }
        return
      }
      console.error('Sign up error:', error)
      form.setError('root', { message: error.message || 'Failed to sign up' })
    },
  })

  return (
    <AuthCard
      title="Create MK8D Account"
      description="Choose your pseudo, then connect your account"
    >
      <AuthForm
        schema={signUpSchema}
        defaultValues={{
          pseudo: '',
          email: '',
          password: '',
          emulatorUserId: '',
        }}
        onSubmit={(data) => signUpMutation.mutate(data)}
        submitText="Sign up"
        loadingText="Signing up..."
        isLoading={signUpMutation.isPending}
        form={form}
      >
        {(form) => (
          <>
            <AuthField
              control={form.control}
              name="pseudo"
              label="Pseudo *"
              placeholder="Your in-game name"
            />

            <AuthField
              control={form.control}
              name="email"
              label="Email"
              placeholder="john@doe.com"
              type="email"
            />

            <AuthField
              control={form.control}
              name="password"
              label="Password"
              placeholder="Enter your password"
              type="password"
            />

            <AuthField
              control={form.control}
              name="emulatorUserId"
              label="Emulator User ID (optional)"
              placeholder="Ryujinx / emulator user ID"
            />
          </>
        )}
      </AuthForm>

      <div className="text-center text-sm mt-4 space-x-1" style={{ color: 'rgba(255,255,255,0.6)' }}>
        <div className="inline-block">Already have an account? </div>
        <div className="inline-block">
          <Link
            to="/sign-in"
            search={search.redirect ? { redirect: search.redirect } : undefined}
            className="font-medium underline-offset-4 hover:underline"
            style={{ color: '#00eaff' }}
          >
            Sign in
          </Link>
        </div>
      </div>
    </AuthCard>
  )
}
